import { IHealthCheckInput } from '../types';
import { STATE_IN_PROGRESS } from './legalTransactionState';

/**
 * Regeln zum Bereich „Stammdaten des Vorgangs" (#1283).
 *
 * Diese Prüfungen gab es bisher nirgends — die Felder sind alle optional, und
 * einige Lücken korrigiert das Backend still (z. B. setzt es den ersten Notar),
 * was niemand mitbekommt.
 */

/**
 * Kein Beurkundungsdatum. Fachlich der wichtigste Fund im Bereich: das Datum
 * ist der Startanker aller Maßnahmen-Fristen, ohne ihn laufen sämtliche Fristen
 * des Vorgangs ohne Startpunkt.
 */
export const missingNotarizationDate = ({
  legalTransaction,
}: IHealthCheckInput) =>
  !legalTransaction.NotarizationDate
    ? [
        'Es ist kein Datum der Beurkundung hinterlegt. Die Fristen der Maßnahmen haben damit keinen Startpunkt.',
      ]
    : [];

/** Weder Sachbearbeiter noch Benutzergruppe zugewiesen. */
export const missingEmployee = ({ legalTransaction }: IHealthCheckInput) =>
  legalTransaction.Employee_idUser === null &&
  legalTransaction.idUserGroup === null
    ? [
        'Dem Vorgang ist kein Sachbearbeiter und keine Benutzergruppe zugewiesen. Es ist damit niemand zuständig.',
      ]
    : [];

/** Keine Gemeinde/Gemarkung — Default für alle Phasen des Vorgangs. */
export const missingAdministrativeUnit = ({
  legalTransaction,
}: IHealthCheckInput) =>
  legalTransaction.idAdministrativeUnit === null ||
  legalTransaction.idAdministrativeUnit === undefined
    ? [
        'Es ist keine Gemeinde/Gemarkung hinterlegt. Sie ist die Vorgabe für alle Phasen des Vorgangs.',
      ]
    : [];

/** Keine Bezeichnung. Wird nur bei der Neuanlage geprüft, nicht beim Bearbeiten. */
export const missingTitle = ({ legalTransaction }: IHealthCheckInput) =>
  !legalTransaction.Title || legalTransaction.Title.trim() === ''
    ? ['Der Vorgang hat keine Bezeichnung.']
    : [];

/**
 * Keine Urkundennummer, obwohl der Vorgang mindestens im Status „Laufend" ist.
 * Leere Einträge werden beim Speichern herausgefiltert und zählen daher nicht.
 */
export const missingRecordNumber = ({
  legalTransaction,
}: IHealthCheckInput) => {
  if (legalTransaction.idLegalTransactionState < STATE_IN_PROGRESS) {
    return [];
  }

  const filled = legalTransaction.RecordNumberArray.filter(
    entry => entry.RecordNumberEntry !== ''
  );

  return filled.length === 0
    ? [
        'Es ist keine Urkundennummer hinterlegt, obwohl der Vorgang bereits läuft.',
      ]
    : [];
};

/** Keine Vorgangsnummer hinterlegt. */
export const missingTransactionNumber = ({
  legalTransaction,
}: IHealthCheckInput) => {
  const filled = legalTransaction.TransactionNumberArray.filter(
    entry => entry.TransactionNumberEntry !== ''
  );

  return filled.length === 0 ? ['Es ist keine Vorgangsnummer hinterlegt.'] : [];
};

/**
 * Hinweis auf die fehlende Portalbereitstellung vor Status „Laufend".
 *
 * Das ist die im Ticket ausdrücklich genannte Ausnahme: der Hinweis wird
 * angezeigt, zählt aber nicht als Warnung. Über die Severity `info` ergibt sich
 * das von selbst, ohne Sonderfall in der Zählung.
 */
export const notYetVisibleForClients = ({
  legalTransaction,
  context,
}: IHealthCheckInput) => {
  if (legalTransaction.idLegalTransactionState >= STATE_IN_PROGRESS) {
    return [];
  }

  const stateName =
    context.legalTransactionStateName ??
    legalTransaction.LegalTransactionState ??
    'unbekannt';

  return [
    `Eine Bereitstellung im Mandantenportal erfolgt nur im Status „Laufend". Im Status „${stateName}" kann der Mandant nicht mit diesem Vorgang interagieren und erhält hierzu keine E-Mails.`,
  ];
};

/**
 * Die Rechtsgeschäftsart wurde im Dialog geändert und der Vorgang muss migriert
 * werden. Erkennbar nur über den gespeicherten Stand aus dem Kontext —
 * `migrateType` am Vorgang wird erst beim Speichern gesetzt.
 */
export const typeChangeNeedsMigration = ({
  legalTransaction,
  context,
}: IHealthCheckInput) => {
  // Ohne den gespeicherten Stand ist eine Aenderung nicht erkennbar — das ist
  // keine Fundfreiheit, sondern eine fehlende Aussage.
  if (context.savedIdLegalTransactionType === undefined) {
    return null;
  }

  return context.savedIdLegalTransactionType !==
    legalTransaction.idLegalTransactionType
    ? [
        'Die Rechtsgeschäftsart wurde verändert. Dieser Vorgang muss zur neuen Rechtsgeschäftsart migriert werden.',
      ]
    : [];
};
